import { useState } from "react";
import type { CaRData, ProtocolFilter } from "../types";
import { InfoTip } from "./InfoTip";
import { downloadCSV } from "../utils/csv";

interface HeatmapSectionProps {
  car: CaRData;
  protocolFilter: ProtocolFilter;
}

type ScenarioId = "eth_crash" | "btc_crash" | "broad_crash" | "lst_depeg" | "ethena_depeg";

const SCENARIO_TABS: { id: ScenarioId; short: string }[] = [
  { id: "eth_crash", short: "ETH Crash" },
  { id: "btc_crash", short: "BTC Crash" },
  { id: "broad_crash", short: "Broad Market" },
  { id: "lst_depeg", short: "LST Depeg" },
  { id: "ethena_depeg", short: "Ethena Depeg" },
];

export function HeatmapSection({ car }: HeatmapSectionProps) {
  const [activeScenario, setActiveScenario] = useState<ScenarioId>("eth_crash");

  const scenario = car.scenarios[activeScenario];
  if (!scenario) return null;

  const drops = scenario.drop_levels ?? car.price_drops_pct;

  // Get assets that have CaR data for this scenario, sorted by total collateral
  const assetsWithRisk = Object.entries(scenario.assets_at_risk)
    .map(([symbol, levels]) => ({
      symbol,
      levels,
      totalCollateral: car.asset_summary[symbol]?.total_collateral_usd ?? 0,
      group: car.asset_summary[symbol]?.group ?? "OTHER",
      avgHF: car.asset_summary[symbol]?.avg_health_factor ?? 0,
      emodePct: car.asset_summary[symbol]?.emode_pct ?? 0,
    }))
    .filter((a) => a.totalCollateral > 0)
    .sort((a, b) => b.totalCollateral - a.totalCollateral);

  // Find max CaR value for color scaling
  const maxCar = Math.max(
    ...scenario.car_by_drop.map((l) => l.car_usd),
    1
  );

  return (
    <div className="card">
      {/* Header with scenario tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold">
            <InfoTip text="Scenario-based stress test. Each cell shows the total collateral value that would enter liquidation if prices dropped by the column percentage. Both collateral and debt are shocked for correlated assets.">
              Liquidation Exposure
            </InfoTip>
          </h2>
          <p className="text-xs text-text-muted mt-0.5">
            {scenario.description}
          </p>
        </div>
        <div className="flex items-center gap-1 bg-surface-700 rounded-lg p-0.5 overflow-x-auto">
          {SCENARIO_TABS.map(({ id, short }) => (
            <button
              key={id}
              onClick={() => setActiveScenario(id)}
              className={`
                px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                ${activeScenario === id
                  ? "bg-accent text-white"
                  : "text-text-secondary hover:text-text-primary hover:bg-surface-600"
                }
              `}
            >
              {short}
            </button>
          ))}
        </div>
      </div>

      {/* Scenario total CaR row */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-8 gap-2 mb-4">
        {scenario.car_by_drop.map((level) => (
          <div key={level.drop_pct} className="card-compact text-center">
            <p className="metric-label">-{level.drop_pct}%</p>
            <p className="text-sm font-mono font-semibold text-text-primary mt-1">
              {formatCompact(level.car_usd)}
            </p>
            <p className="text-xs text-text-muted font-mono">
              {level.positions_liquidated.toLocaleString()} pos
            </p>
          </div>
        ))}
      </div>

      {/* Per-asset breakdown table */}
      <div className="flex justify-end mb-1">
        <button
          onClick={() => downloadCSV(
            assetsWithRisk.map(({ symbol, levels, totalCollateral, group, avgHF, emodePct }) => {
              const row: Record<string, string | number | null> = {
                asset: symbol,
                group: group,
                total_collateral_usd: Math.round(totalCollateral),
                avg_hf: avgHF,
                emode_pct: emodePct,
              };
              drops.forEach((d, i) => {
                row[`car_at_${d}pct`] = i < levels.length ? levels[i].car_usd : 0;
              });
              return row;
            }),
            `liquidation_exposure_${activeScenario}.csv`
          )}
          className="text-[10px] text-accent hover:text-accent/80 transition-colors"
        >
          ↓ CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-text-muted">
              <th className="text-left py-2 pr-4 font-medium">Collateral Token</th>
              <th className="text-right py-2 px-1 font-medium text-text-muted/60">
                <InfoTip position="below" text="Total USD value of this asset deposited as collateral across all positions.">
                  Total Locked
                </InfoTip>
              </th>
              <th className="text-right py-2 px-1 font-medium text-text-muted/60">
                <InfoTip position="below" text="Collateral-weighted average health factor for positions using this asset, capped at 20 per position.">
                  Avg HF
                </InfoTip>
              </th>
              <th className="text-right py-2 px-1 font-medium text-text-muted/60">
                <InfoTip position="below" text="Fraction of this token's collateral held by wallets in Aave E-Mode. Higher E-Mode % means higher leverage but also correlation protection against market crashes.">
                  E-Mode
                </InfoTip>
              </th>
              {drops.map((d) => (
                <th key={d} className="text-right py-2 px-2 font-medium">
                  -{d}%
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {assetsWithRisk.map(({ symbol, levels, totalCollateral, group, avgHF, emodePct }) => (
              <tr key={symbol} className="border-t border-surface-600">
                <td className="py-2 pr-4 font-sans">
                  <span className="font-medium text-text-primary">{symbol}</span>
                  <span className="ml-1.5 text-text-muted/50 text-[10px]">{group}</span>
                </td>
                <td className="text-right py-2 px-1 text-text-muted/60">
                  {formatCompact(totalCollateral)}
                </td>
                <td className={`text-right py-2 px-1 font-mono ${hfColor(avgHF)}`}>
                  {avgHF > 0 ? avgHF.toFixed(2) : "—"}
                </td>
                <td className="text-right py-2 px-1 font-mono text-text-muted">
                  {emodePct > 0 ? `${(emodePct * 100).toFixed(0)}%` : "—"}
                </td>
                {drops.map((dropPct, i) => {
                  const level = levels[i];
                  const carUsd = level?.car_usd ?? 0;
                  return (
                    <td
                      key={dropPct}
                      className="text-right py-2 px-2"
                      style={{ backgroundColor: carCellColor(carUsd, maxCar) }}
                      title={
                        level
                          ? `${level.positions_liquidated} positions liquidated`
                          : "0 positions"
                      }
                    >
                      {carUsd > 0 ? formatCompact(carUsd) : "$0"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-2 mt-3 text-xs text-text-muted">
        <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: "rgba(34, 197, 94, 0.08)" }} /> Low
        <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: "rgba(234, 179, 8, 0.2)" }} /> Medium
        <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: "rgba(239, 68, 68, 0.35)" }} /> High
        <span className="ml-auto">CaR = collateral value entering liquidation at current prices</span>
      </div>
    </div>
  );
}

function hfColor(hf: number): string {
  if (hf === 0) return "text-text-muted";
  if (hf < 1.1) return "text-risk-danger";
  if (hf < 1.3) return "text-risk-warning";
  if (hf < 2.0) return "text-risk-moderate";
  return "text-risk-safe";
}

function carCellColor(car_usd: number, maxCar: number): string {
  if (car_usd === 0) return "transparent";
  const ratio = car_usd / maxCar;
  if (ratio < 0.01) return "rgba(34, 197, 94, 0.08)";
  if (ratio < 0.03) return "rgba(234, 179, 8, 0.12)";
  if (ratio < 0.08) return "rgba(234, 179, 8, 0.20)";
  if (ratio < 0.15) return "rgba(249, 115, 22, 0.25)";
  if (ratio < 0.30) return "rgba(239, 68, 68, 0.30)";
  return "rgba(220, 38, 38, 0.40)";
}

function formatCompact(value: number): string {
  if (value === 0) return "$0";
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
